import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import {
  SESSION_IDLE_TIMEOUT_MS,
  SESSION_ABSOLUTE_TIMEOUT_MS,
  SESSION_ACTIVITY_CHECK_INTERVAL_MS,
  SESSION_ACTIVITY_WRITE_THROTTLE_MS,
  SESSION_LAST_ACTIVITY_KEY,
  SESSION_EXPIRED_FLAG,
} from "./config";

// How long the "Login as" impersonation handoff suppresses the session-expiry
// watchdog (idle / absolute / 401). Must comfortably cover window-open + page-load +
// token-exchange POST + login() + navigation + settle. Self-expiring so a stale
// guard can never permanently disable expiry. Technical timing constant.
const IMPERSONATION_GUARD_MS = 15000;

const AuthContext = createContext();

// ── Centralized 401 interceptor ─────────────────────────────────────────────
// The admin app uses fetch() everywhere (no axios), so rather than touch every
// call site we patch window.fetch ONCE. Any 401 from one of our own admin API
// endpoints (while a token exists) means the session is no longer valid, so we
// route it through a single session-expiry handler. We deliberately ignore
// non-admin hosts (reCAPTCHA, Stripe, LinkedIn, …) and the login/forgot-password
// endpoints, whose 401s are normal credential failures rather than expiry.
let onSessionUnauthorized = null;

const extractUrl = (input) => {
  try {
    if (typeof input === "string") return input;
    if (input && typeof input.url === "string") return input.url;
    if (input) return String(input);
  } catch {
    /* ignore */
  }
  return "";
};

if (typeof window !== "undefined" && !window.__cpAdminFetchPatched) {
  window.__cpAdminFetchPatched = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    try {
      const url = extractUrl(args[0]);
      const isAdminApi = url.includes("/api-recruitment-admin");
      const isAuthEndpoint =
        url.includes("/recruiters/login") || url.includes("/recruiters/forgot-password");
      if (
        response.status === 401 &&
        isAdminApi &&
        !isAuthEndpoint &&
        localStorage.getItem("token") &&
        typeof onSessionUnauthorized === "function"
      ) {
        onSessionUnauthorized();
      }
    } catch {
      /* never let interceptor bookkeeping break a real request */
    }
    return response;
  };
}

const storedToken = localStorage.getItem("token");

if (storedToken) {
  try {
    const decoded = jwtDecode(storedToken);
    const now = Date.now() / 1000;

    if (!decoded?.exp || decoded.exp <= now) {
      // Flag genuinely-expired sessions so the login screen can explain why.
      if (decoded?.exp && decoded.exp <= now) {
        try {
          sessionStorage.setItem(SESSION_EXPIRED_FLAG, "1");
        } catch {
          /* ignore */
        }
      }
      localStorage.removeItem("token");
      localStorage.removeItem("recruiterId");
      localStorage.removeItem("recruiterEmail");
      localStorage.removeItem(SESSION_LAST_ACTIVITY_KEY);
    }
  } catch {
    localStorage.removeItem("token");
    localStorage.removeItem("recruiterId");
    localStorage.removeItem("recruiterEmail");
    localStorage.removeItem(SESSION_LAST_ACTIVITY_KEY);
  }
}

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem("token") || null);
  const [recruiterEmail, setRecruiterEmail] = useState(localStorage.getItem("recruiterEmail") || null);
  const [recruiterId, setRecruiterId] = useState(localStorage.getItem("recruiterId") || null);

  const login = useCallback((token, id, email) => {
    localStorage.setItem("token", token);
    localStorage.setItem("recruiterId", id);
    localStorage.setItem("recruiterEmail", email);
    localStorage.setItem(SESSION_LAST_ACTIVITY_KEY, String(Date.now()));
    // A successful login is a fresh, valid session: clear any stale "session
    // expired" notice, and clear the impersonation flag so a NORMAL login is never
    // treated as an admin impersonation (ImpersonatePage re-sets it after this).
    // The flag is written to sessionStorage (see expireSession + the module check),
    // so it MUST be cleared there — clearing localStorage alone was a no-op, which
    // is why a stale "session expired" notice kept showing after a fresh login.
    sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
    localStorage.removeItem(SESSION_EXPIRED_FLAG);
    localStorage.removeItem("impersonating");
    // Do NOT clear "impersonation_in_progress" here — it's a self-expiring timestamp
    // owned by ImpersonatePage that must stay set across the WHOLE handoff (including
    // the post-login settle window) so the expiry watchdog can't clobber the freshly
    // installed impersonation session. It auto-expires after IMPERSONATION_GUARD_MS.
    setToken(token);
    setRecruiterId(id);
    setRecruiterEmail(email);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("recruiterId");
    localStorage.removeItem("recruiterEmail");
    localStorage.removeItem(SESSION_LAST_ACTIVITY_KEY);
    localStorage.removeItem("impersonating");
    setToken(null);
    setRecruiterId(null);
    setRecruiterEmail(null);
  }, []);

  // Sign out AND leave a one-shot flag so the login screen can show a friendly
  // "your session expired" notice. ProtectedRoute redirects to /login on its
  // own once token becomes null, so we don't need router access here.
  const expireSession = useCallback(() => {
    const tok = localStorage.getItem("token");
    if (!tok) return; // already signed out
    // During an admin "Login as" handoff the OLD session's idle/absolute/401 watchdog
    // must NOT fire — it would expire the token mid-swap and bounce the admin to
    // "session expired" before the impersonation token is installed. This was the
    // intermittent "sometimes works, sometimes session expired" bug. ImpersonatePage
    // stamps a timestamp; suppress expiry for IMPERSONATION_GUARD_MS after it (self-
    // expiring, so a stale guard can never permanently disable expiry).
    const inProg = Number(localStorage.getItem("impersonation_in_progress"));
    if (inProg && Date.now() - inProg < IMPERSONATION_GUARD_MS) return;
    // Belt-and-suspenders: never expire a freshly-minted session (just-installed
    // impersonation or login token). A stale timer from the prior token could
    // otherwise fire moments after the swap and clobber the new session.
    try {
      const decoded = jwtDecode(tok);
      if (decoded?.iat && Date.now() - decoded.iat * 1000 < IMPERSONATION_GUARD_MS) return;
    } catch {
      /* fall through to normal expiry */
    }
    try {
      sessionStorage.setItem(SESSION_EXPIRED_FLAG, "1");
    } catch {
      /* ignore */
    }
    logout();
  }, [logout]);

  // Absolute JWT-exp timer: hard logout the moment the backend token expires.
  useEffect(() => {
    let timer = null;

    if (token) {
      try {
        const decoded = jwtDecode(token);
        const now = Date.now() / 1000;

        if (!decoded?.exp || decoded.exp <= now) {
          expireSession();
        } else {
          const timeUntilExpiry = (decoded.exp - now) * 1000;
          timer = setTimeout(() => {
            expireSession();
          }, timeUntilExpiry);
        }
      } catch (error) {
        console.error("Invalid token:", error);
        logout();
      }
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [token, expireSession, logout]);

  // Idle + absolute-cap watchdog. Tracks user activity and periodically (plus on
  // tab focus / visibility) checks whether the session has gone idle past the
  // configured window or outlived the absolute cap — covering the "left a tab
  // open for days, came back" case where a plain setTimeout would have been
  // throttled/paused while the machine slept.
  useEffect(() => {
    if (!token) return;

    const markActivity = () => {
      const now = Date.now();
      const last = Number(localStorage.getItem(SESSION_LAST_ACTIVITY_KEY)) || 0;
      if (now - last >= SESSION_ACTIVITY_WRITE_THROTTLE_MS) {
        localStorage.setItem(SESSION_LAST_ACTIVITY_KEY, String(now));
      }
    };

    const checkTimeouts = () => {
      const now = Date.now();

      const last = Number(localStorage.getItem(SESSION_LAST_ACTIVITY_KEY)) || now;
      if (now - last >= SESSION_IDLE_TIMEOUT_MS) {
        expireSession();
        return;
      }

      try {
        const decoded = jwtDecode(token);
        if (decoded?.iat) {
          const sessionAge = now - decoded.iat * 1000;
          if (sessionAge >= SESSION_ABSOLUTE_TIMEOUT_MS) {
            expireSession();
          }
        }
      } catch {
        /* token integrity is handled by the absolute-exp effect above */
      }
    };

    // Seed an activity timestamp if missing (e.g. straight after a hard reload).
    if (!localStorage.getItem(SESSION_LAST_ACTIVITY_KEY)) {
      localStorage.setItem(SESSION_LAST_ACTIVITY_KEY, String(Date.now()));
    }

    const activityEvents = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];
    activityEvents.forEach((evt) =>
      window.addEventListener(evt, markActivity, { passive: true })
    );

    const onVisibility = () => {
      if (document.visibilityState === "visible") checkTimeouts();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", checkTimeouts);

    const interval = setInterval(checkTimeouts, SESSION_ACTIVITY_CHECK_INTERVAL_MS);

    // Catch a session that is already stale on this very mount.
    checkTimeouts();

    return () => {
      activityEvents.forEach((evt) =>
        window.removeEventListener(evt, markActivity)
      );
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", checkTimeouts);
      clearInterval(interval);
    };
  }, [token, expireSession]);

  // Let the global fetch 401-interceptor reach into the live session handler.
  useEffect(() => {
    onSessionUnauthorized = expireSession;
    return () => {
      if (onSessionUnauthorized === expireSession) onSessionUnauthorized = null;
    };
  }, [expireSession]);

  return (
    <AuthContext.Provider value={{ token, recruiterId, recruiterEmail, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);